/**
 * reconnectInstance — Reconecta uma instância desconectada gerando novo QR Code.
 *
 * Fluxo Uazapi:
 * 1. POST /instance/connect (sem body) → inicia sessão e pode retornar QR code
 * 2. GET /instance/status → retorna QR code atualizado durante connecting
 *
 * Se o QR não vem imediatamente, faz polling via /instance/status.
 */
import { fetchAPI } from './client';

export interface ReconnectResult {
    base64: string | null;
    code: string | null;
    connected: boolean;
}

/**
 * Solicita reconexão e busca QR Code com polling.
 * Retorna QR code base64 ou null se já está conectado.
 */
export async function reconnectAndGetQR(instanceName: string): Promise<ReconnectResult> {
    console.log(`[Reconnect] Iniciando reconexão: ${instanceName}`);

    // 1. Chama /instance/connect para iniciar a sessão
    try {
        const connectRes = await fetchAPI<any>('instance/connect', {
            method: 'POST',
            headers: { 'instance': instanceName }
        });

        console.log('[Reconnect] Resposta connect:', JSON.stringify(connectRes).substring(0, 200));

        // Se já está conectado
        if (connectRes?.connected === true || connectRes?.status?.connected === true) {
            return { base64: null, code: null, connected: true };
        }

        // Se veio QR code direto na resposta
        const qr = extractQRFromResponse(connectRes);
        if (qr) {
            return { base64: qr.base64, code: qr.code, connected: false };
        }
    } catch (e: any) {
        console.warn('[Reconnect] Erro no connect (tentando status):', e.message);
    }

    // 2. Polling via /instance/status para pegar o QR code
    for (let attempt = 0; attempt < 8; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 2000));

        try {
            const status = await fetchAPI<any>('instance/status', {
                method: 'GET',
                headers: { 'instance': instanceName }
            });

            console.log(`[Reconnect] Status attempt ${attempt + 1}:`, JSON.stringify(status).substring(0, 200));

            // Se conectou durante o polling
            if (status?.status?.connected === true || status?.instance?.status === 'connected') {
                return { base64: null, code: null, connected: true };
            }

            // Tenta extrair QR code
            const qr = extractQRFromResponse(status);
            if (qr) {
                return { base64: qr.base64, code: qr.code, connected: false };
            }
        } catch (e: any) {
            console.warn(`[Reconnect] Erro status attempt ${attempt + 1}:`, e.message);
        }
    }

    // 3. Última tentativa: outro connect
    try {
        const retry = await fetchAPI<any>('instance/connect', {
            method: 'POST',
            headers: { 'instance': instanceName }
        });
        const qr = extractQRFromResponse(retry);
        if (qr) {
            return { base64: qr.base64, code: qr.code, connected: false };
        }
    } catch { /* silencio */ }

    return { base64: null, code: null, connected: false };
}

/**
 * Extrai QR code de qualquer formato de resposta da Uazapi.
 */
function extractQRFromResponse(data: any): { base64: string; code: string | null } | null {
    if (!data) return null;

    const base64 = data.base64
        || data.qrcode
        || data.instance?.qrcode
        || data.qrcode?.base64
        || data.status?.qrcode;

    if (base64 && typeof base64 === 'string' && base64.length > 50) {
        return {
            base64,
            code: data.pairingCode || data.code || null
        };
    }

    return null;
}
